import { useMemo, useState } from 'react';
import { Button, Col, DateTimeInfo, Dropdown, More2LineIcon, Row, Skeleton, Space } from 'ui';
import { EditEvent, EventSocialContent, useEventDropdownActions, useEventQuery } from 'features/events';
import { Description } from '../description';
import { Participants } from '../participants';
import { Repeats } from '../repeats';
import { Tag } from '../tag';
import { Title } from '../title';
import { Type } from '../type';

type ModalAction = 'duplicate' | 'edit';

type P = {
  eventId: string;
  getPathToEventDetail: (id: string) => string;
};

const RailsEvent = ({ eventId, getPathToEventDetail }: P) => {
  const { data: eventData, isLoading: isLoadingEvent } = useEventQuery({
    id: eventId,
  });

  const [modalAction, setModalAction] = useState<ModalAction>();

  const handleRequestAction = (action: ModalAction) => {
    setModalAction(action);
  };

  const { contextHolders, getActions } = useEventDropdownActions({
    onRequestAction: handleRequestAction,
  });

  const actions = useMemo(() => {
    if (!eventData) {
      return [];
    }
    return getActions({ eventContentObject: eventData });
  }, [eventData, getActions]);

  return (
    <Space block orientation="vertical" size="middle">
      {isLoadingEvent || !eventData ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : (
        <>
          <Row justify="space-between">
            <Col>
              <Tag
                endTime={eventData.event.endTime}
                startTime={eventData.event.startTime}
                type={eventData.event.eventType}
              />
            </Col>
            {actions.length > 0 ? (
              <Col>
                <Dropdown menu={{ items: actions }} trigger={['click']}>
                  <Button icon={<More2LineIcon />} type="link-secondary" />
                </Dropdown>
              </Col>
            ) : null}
          </Row>
          <Title
            getPathToEventDetail={getPathToEventDetail}
            id={eventData.event.id}
            status={eventData.event.status}
            title={eventData.event.name}
          />
          <DateTimeInfo
            timeInfo={{
              dateFrom: eventData.event.startTime,
              dateTo: eventData.event.endTime,
            }}
          />
          <Repeats />
          <Type />
          <Participants
            eventType={eventData.event.eventType}
            numberOfParticipants={eventData.event.goingInviteesCount}
          />
          <Description htmlContent={eventData.event.htmlContent} />
          <EventSocialContent authorId={eventData.author.nodeUserId} event={eventData.event} />
          <EditEvent
            eventId={eventData.event.id}
            isOpened={modalAction !== undefined}
            mode={modalAction ?? 'edit'}
            onCancel={() => {
              setModalAction(undefined);
            }}
            onSuccess={() => {
              setModalAction(undefined);
            }}
          />
        </>
      )}

      {contextHolders}
    </Space>
  );
};

export default RailsEvent;
